import { useEffect, useMemo } from 'react'
import {
    Controller,
    FormProvider,
    useFieldArray,
    useForm,
    useWatch,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { SalesReturnFormValues } from '@/lib/schemas/sales-return-schemas'
import {
    createSalesReturnSchema,
    toCreateReturnPayload,
    toPreviewItems,
    toSalesReturnFormValues,
} from '@/lib/schemas/sales-return-schemas'
import { refundFieldErrors, toRefundChoice } from '@/lib/schemas/refund-schemas'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { RefundSection } from '@/components/sections/refund-section'
import { SalesReturnLine } from '@/components/sections/sales-return-line'
import { ReturnFeeSection } from '@/components/sections/return-fee-section'
import { ReturnQtyStepper } from '@/components/sections/return-qty-stepper'
import { useAuth } from '@/hooks/use-auth'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useCreateSalesReturn,
    useOrderReturnable,
    useReturnPreview,
} from '@/hooks/use-sales-return-query'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

interface Props {
    open: boolean
    orderId: string
    onClose: () => void
    onSuccess?: () => void
}

const EMPTY: SalesReturnFormValues = {
    note: '',
    lines: [],
    packaging: [],
    refund: { mode: 'none', amount: '', payment_method: '' },
    fee_amount: '',
    fee_reason: '',
}

/**
 * Popup "Trả hàng" cho đơn đã giao (M-44): trả một phần, nhiều lần. Hàng về
 * kho theo lô (kệ, hàng lỗi như "Xác nhận hoàn"), giá trị trả giảm công nợ của
 * đơn; khách đã trả thừa thì chọn không hoàn / hoàn toàn bộ / hoàn một phần.
 */
export function SalesReturnDialog({
    open,
    orderId,
    onClose,
    onSuccess,
}: Props) {
    const { data, isLoading } = useOrderReturnable(orderId, open)
    const createReturn = useCreateSalesReturn()
    const { hasPermission } = useAuth()
    const workflow = useWorkflowTracking('order_return', {
        active: open,
        attemptKey: orderId,
        operation: 'create',
    })
    const form = useForm<SalesReturnFormValues>({
        resolver: zodResolver(createSalesReturnSchema()),
        defaultValues: EMPTY,
    })
    const {
        control,
        handleSubmit,
        reset,
        register,
        setError,
        setValue,
        formState,
    } = form
    const { fields } = useFieldArray({ control, name: 'lines' })
    const packaging = useWatch({ control, name: 'packaging' })
    const lines = useWatch({ control, name: 'lines' })
    const refund = useWatch({ control, name: 'refund' })

    useEffect(() => {
        if (open && data) reset(toSalesReturnFormValues(data))
    }, [open, data, reset])

    const fee = useWatch({ control, name: 'fee_amount' })
    const previewItems = useDebounced(
        useMemo(() => toPreviewItems(lines), [lines]),
    )
    const feeNumber = useDebounced(Number(fee) > 0 ? Number(fee) : 0)
    const { data: preview } = useReturnPreview(
        orderId,
        previewItems,
        open,
        feeNumber,
    )
    const hasItems = previewItems.length > 0
    const refundable = hasItems ? (preview?.refundable_after ?? 0) : 0
    // Phí chỉ lấy từ tiền khách đã trả dư: đơn chưa thu đủ thì không có phí.
    const feeMax = hasItems ? (preview?.fee_max ?? 0) : 0
    useEffect(() => {
        // Bỏ số trả làm hết phần có thể giữ lại → xoá phí đã nhập để form
        // không còn đòi lý do cho một ô đang ẩn.
        if (preview && feeMax <= 0 && fee !== '') {
            setValue('fee_amount', '')
            setValue('fee_reason', '')
        }
    }, [preview, feeMax, fee, setValue])

    const onSubmit = handleSubmit(async (values) => {
        const feeValue = Number(values.fee_amount) || 0
        if (preview && feeValue > preview.fee_max) {
            setError('fee_amount', {
                message: m.sales_return_err_fee_max({
                    max: formatVnd(preview.fee_max),
                }),
            })
            return
        }
        const refundErrs =
            refundable > 0 ? refundFieldErrors(values.refund, refundable) : {}
        if (refundErrs.amount)
            setError('refund.amount', { message: refundErrs.amount })
        if (refundErrs.payment_method)
            setError('refund.payment_method', {
                message: refundErrs.payment_method,
            })
        if (Object.keys(refundErrs).length > 0) return
        const payload = {
            ...toCreateReturnPayload(values),
            refund: refundable > 0 ? toRefundChoice(values.refund) : undefined,
        }
        await workflow.run(
            () => createReturn.mutateAsync({ orderId, data: payload }),
            (result) => result.data?.id,
        )
        onSuccess?.()
        onClose()
    })

    const refundErrors = {
        amount: formState.errors.refund?.amount?.message,
        payment_method: formState.errors.refund?.payment_method?.message,
    }
    const noLines = !isLoading && fields.length === 0
    // Lỗi cấp mảng (chưa trả dòng nào): RHF có thể đặt ở `lines` hoặc
    // `lines.root` tuỳ field array — đọc cả hai.
    const linesError =
        formState.errors.lines?.message ?? formState.errors.lines?.root?.message

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                onChangeCapture={() => workflow.interact('items')}
                className={twJoin(
                    'flex max-h-[92dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl',
                    // Mobile: bám đáy màn như bottom sheet, không lơ lửng giữa.
                    'max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-b-none',
                )}
            >
                <DialogHeader className="border-b px-4 pt-5 pb-3 pr-12 text-left sm:px-6">
                    <DialogTitle>{m.sales_return_title()}</DialogTitle>
                    <DialogDescription>
                        {m.sales_return_desc()}
                    </DialogDescription>
                </DialogHeader>
                <FormProvider {...form}>
                    <form
                        noValidate
                        onSubmit={onSubmit}
                        className="flex min-h-0 flex-1 flex-col"
                    >
                        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
                            {isLoading ? (
                                <p className="text-sm text-muted-foreground">
                                    {m.confirm_return_loading()}
                                </p>
                            ) : noLines ? (
                                <p className="text-sm text-muted-foreground">
                                    {m.sales_return_nothing_left()}
                                </p>
                            ) : (
                                <section className="space-y-3">
                                    {fields.map((f, i) => (
                                        <SalesReturnLine key={f.id} index={i} />
                                    ))}
                                    {linesError && (
                                        <p
                                            className="text-sm text-destructive"
                                            role="alert"
                                        >
                                            {linesError}
                                        </p>
                                    )}
                                </section>
                            )}

                            {packaging.length > 0 && (
                                <section className="space-y-2">
                                    <h3 className="text-sm font-semibold">
                                        {m.confirm_return_section_packaging()}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        {m.sales_return_packaging_hint()}
                                    </p>
                                    {packaging.map((p, i) => (
                                        <div
                                            key={p.packaging_lot_id}
                                            className="space-y-2 rounded-lg border p-3"
                                        >
                                            <label className="flex min-h-11 cursor-pointer items-center justify-between gap-3 text-sm">
                                                <span className="min-w-0">
                                                    <span className="font-medium">
                                                        {p.material_name}
                                                    </span>
                                                    <span className="block font-mono text-xs text-muted-foreground">
                                                        {p.lot_code} ·{' '}
                                                        {m.confirm_return_col_deducted()}{' '}
                                                        {p.deducted_qty}{' '}
                                                        {p.material_unit}
                                                    </span>
                                                </span>
                                                <Controller
                                                    control={control}
                                                    name={`packaging.${i}.enabled`}
                                                    render={({ field }) => (
                                                        <Switch
                                                            checked={
                                                                field.value
                                                            }
                                                            onCheckedChange={
                                                                field.onChange
                                                            }
                                                        />
                                                    )}
                                                />
                                            </label>
                                            {p.enabled && (
                                                <Controller
                                                    control={control}
                                                    name={`packaging.${i}.quantity`}
                                                    render={({
                                                        field,
                                                        fieldState,
                                                    }) => (
                                                        <div>
                                                            <ReturnQtyStepper
                                                                id={`return-packaging-${p.packaging_lot_id}`}
                                                                label={m.confirm_return_col_restore()}
                                                                value={
                                                                    field.value
                                                                }
                                                                onChange={
                                                                    field.onChange
                                                                }
                                                                max={
                                                                    p.deducted_qty
                                                                }
                                                                unit={
                                                                    p.material_unit
                                                                }
                                                                size="sm"
                                                                invalid={Boolean(
                                                                    fieldState.error,
                                                                )}
                                                            />
                                                            {fieldState.error && (
                                                                <p className="mt-1 text-sm text-destructive">
                                                                    {
                                                                        fieldState
                                                                            .error
                                                                            .message
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </section>
                            )}

                            {feeMax > 0 && <ReturnFeeSection feeMax={feeMax} />}

                            {hasItems && (
                                <RefundSection
                                    value={refund}
                                    onChange={(v) =>
                                        form.setValue('refund', v, {
                                            shouldDirty: true,
                                        })
                                    }
                                    maxRefundable={refundable}
                                    canRefund={hasPermission(
                                        'customers.refund',
                                    )}
                                    errors={refundErrors}
                                />
                            )}

                            <Field
                                name="note"
                                label={m.sales_return_note_label()}
                            >
                                <Textarea
                                    id="note"
                                    rows={2}
                                    className="text-base sm:text-sm"
                                    {...register('note')}
                                />
                            </Field>
                        </div>

                        <div className="space-y-3 border-t bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
                            {hasItems && preview && (
                                <dl
                                    className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm"
                                    aria-live="polite"
                                >
                                    <dt className="text-muted-foreground">
                                        {m.sales_return_value_label()}
                                    </dt>
                                    <dd className="text-right font-semibold tabular-nums">
                                        {formatVnd(preview.total_amount)}
                                    </dd>
                                    {preview.fee_amount > 0 && (
                                        <>
                                            <dt className="text-muted-foreground">
                                                {m.sales_return_fee_kept_label()}
                                            </dt>
                                            <dd className="text-right tabular-nums">
                                                {formatVnd(preview.fee_amount)}
                                            </dd>
                                        </>
                                    )}
                                    <dt className="text-muted-foreground">
                                        {m.sales_return_remaining_after()}
                                    </dt>
                                    <dd className="text-right tabular-nums">
                                        {formatVnd(preview.remaining_after)}
                                    </dd>
                                </dl>
                            )}
                            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="h-11 sm:h-9"
                                    onClick={onClose}
                                    disabled={createReturn.isPending}
                                >
                                    {m.confirm_return_cancel_btn()}
                                </Button>
                                <Button
                                    type="submit"
                                    className="h-11 sm:h-9"
                                    disabled={
                                        isLoading || createReturn.isPending
                                    }
                                >
                                    {createReturn.isPending
                                        ? m.confirm_return_processing()
                                        : m.sales_return_submit()}
                                </Button>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}
